import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, CircularProgress, Collapse } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import ProductionTracking from "components/ProductionTracking";
import { getMyPublicationOrders } from "services/orders";
import { isPaid, printerStatusInfo, parsePrinterStatus } from "shared/docuprintStatus";
import "./orders.css";

const currency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(String(value || 0).replace(",", ".")) || 0
  );
const dateLabel = (order) => {
  const date = String(order.date || "").slice(0, 10),
    parts = date.split("-");
  return `${parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : date}${
    order.hour ? ` · ${String(order.hour).slice(0, 5)}` : ""
  }`;
};
const cancelled = (order) => /cancel|estorn/i.test(String(order.status || ""));
function progressLabel(order) {
  const printer = order.printer;
  if (printer?.id && parsePrinterStatus(printer.providerStatus) !== null)
    return printerStatusInfo(printer.providerStatus).title;
  if (cancelled(order)) return "Pedido cancelado";
  if (!isPaid(order.paymentStatus)) return "Aguardando pagamento";
  if (printer?.id) return "Consultando andamento na gráfica";
  if (printer?.state === "blocked") return "Pendência no envio à gráfica";
  if (printer?.state === "uncertain" || printer?.state === "sending")
    return "Conferindo envio à gráfica";
  if (printer?.state === "awaiting_invoice") return "Preparando documentos";
  if (printer?.state === "unavailable") return "Acompanhamento indisponível";
  return "Preparando envio à gráfica";
}
function OrderCard({ order }) {
  const [expanded, setExpanded] = useState(false);
  const paid = isPaid(order.paymentStatus),
    printer = order.printer;
  const items = order.production?.length
    ? order.production
    : order.books?.length
    ? order.books
    : (order.bookCodes || []).map((code) => ({ code }));
  const label = progressLabel(order);
  const manualItems = (order.production || []).filter(
    (item) => item.stage && item.stage !== "received"
  );
  const detailsId = `order-tracking-${order.id}`;
  return (
    <article className="order-card" aria-label={`Pedido ${order.id}`}>
      <header className="order-card__header">
        <div className="order-card__identity">
          <span className="order-card__icon" aria-hidden="true">
            <ReceiptLongOutlinedIcon />
          </span>
          <div>
            <h2>Pedido #{order.id}</h2>
            <time>{dateLabel(order)}</time>
          </div>
        </div>
        <div className="order-card__header-right">
          <span className={`order-pill ${paid ? "order-pill--paid" : "order-pill--pending"}`}>
            {paid ? <CheckCircleOutlineRoundedIcon /> : <AccessTimeRoundedIcon />}
            {paid ? "Pagamento confirmado" : "Aguardando pagamento"}
          </span>
          <strong className="order-card__amount">{currency(order.totalAmount)}</strong>
        </div>
      </header>
      <div className="order-card__body">
        <div>
          {items.length ? (
            items.map((item) => (
              <div className="order-card__book" key={item.code}>
                <div className="order-card__book-title">{item.title || "Livro publicado"}</div>
                <span className="order-card__book-code">
                  {String(item.code).startsWith("PSO-") ? item.code : `PSO ${item.code}`}
                </span>
              </div>
            ))
          ) : (
            <div className="order-card__book-title">Publicação vinculada ao pedido</div>
          )}
        </div>
        <div className="order-card__progress">
          <span>{printer?.id ? "PRODUÇÃO NA GRÁFICA" : "ANDAMENTO"}</span>
          <strong
            className={
              printer?.state === "blocked" ||
              [-1, 6].includes(parsePrinterStatus(printer?.providerStatus)) ||
              cancelled(order)
                ? "is-alert"
                : ""
            }
          >
            <i aria-hidden="true" />
            {label}
          </strong>
        </div>
      </div>
      <footer className="order-card__footer">
        <span className="order-card__payment">
          <CreditCardRoundedIcon />
          {order.paymentMethod || "Forma de pagamento não informada"}
        </span>
        <button
          type="button"
          className="order-card__toggle"
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={() => setExpanded(!expanded)}
          aria-label={`${expanded ? "Ocultar" : "Ver"} acompanhamento do pedido ${order.id}`}
        >
          {expanded ? "Ocultar acompanhamento" : "Ver acompanhamento"}
          <ExpandMoreRoundedIcon />
        </button>
      </footer>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className="order-card__tracking" id={detailsId}>
          {!printer?.id && manualItems.length > 0 ? (
            manualItems.map((item) => (
              <div key={item.code}>
                <p className="order-card__book-title">{item.title}</p>
                <ProductionTracking
                  item={item}
                  paid={paid}
                  printer={printer}
                  cancelled={cancelled(order)}
                />
              </div>
            ))
          ) : (
            <ProductionTracking
              item={items[0]}
              printer={printer}
              paid={paid}
              cancelled={cancelled(order)}
            />
          )}
        </div>
      </Collapse>
    </article>
  );
}
const FIRST_QUERY = { page: 1, payment: "all", search: "" };
function MyOrders({ user }) {
  const [result, setResult] = useState(null),
    [error, setError] = useState("");
  const [loading, setLoading] = useState(true),
    [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState(FIRST_QUERY),
    [search, setSearch] = useState("");
  const sequence = useRef(0),
    controller = useRef(null),
    busy = useRef(false),
    owner = useRef(user?.uid),
    heading = useRef(null);
  const queryKey = JSON.stringify(query);
  const current = result?.uid === user?.uid && result?.key === queryKey;
  const orders = current ? result.orders : [];
  const visible = orders;
  const summary = result && result.uid === user?.uid ? result.summary : null;
  const paging = current ? result.paging : { page: query.page, limit: 20, total: 0, totalPages: 1 };
  const filter = query.payment;
  const hasFilters = Boolean(query.search || filter !== "all");
  useEffect(() => {
    if (owner.current !== user?.uid) {
      owner.current = user?.uid;
      setResult(null);
      setSearch("");
      setQuery(FIRST_QUERY);
    }
  }, [user?.uid]);
  useEffect(() => {
    if (search.trim() === query.search) return undefined;
    const timer = setTimeout(
      () => setQuery((previous) => ({ ...previous, page: 1, search: search.trim() })),
      350
    );
    return () => clearTimeout(timer);
  }, [search, query.search]);
  const loadOrders = useCallback(
    async ({ silent = false, discover = false, refreshPayment = false } = {}) => {
      if (!user?.uid) return;
      controller.current?.abort();
      const abort = new AbortController();
      controller.current = abort;
      const request = ++sequence.current;
      busy.current = true;
      setLoading(!silent);
      setRefreshing(silent);
      setError("");
      try {
        const body = await getMyPublicationOrders({
          authId: user.uid,
          page: query.page,
          limit: 20,
          payment: query.payment,
          search: query.search,
          refreshSaved: refreshPayment,
          sync: discover,
          signal: abort.signal,
        });
        if (request !== sequence.current || abort.signal.aborted) return;
        if (!body.summary || !body.paging)
          throw new Error("Não foi possível carregar a lista de pedidos. Tente novamente.");
        if (body.paging.page !== query.page) {
          setQuery((previous) => ({ ...previous, page: body.paging.page }));
          return;
        }
        setResult({
          uid: user.uid,
          key: queryKey,
          orders: body.orders || [],
          paging: body.paging,
          summary: body.summary,
        });
      } catch (failure) {
        if (request === sequence.current && !abort.signal.aborted)
          setError(failure.message || "Não foi possível carregar os pedidos.");
      } finally {
        if (request === sequence.current) {
          busy.current = false;
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [user?.uid, query, queryKey]
  );
  useEffect(() => {
    loadOrders();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible" && !busy.current) loadOrders({ silent: true });
    }, 60000);
    return () => {
      clearInterval(timer);
      sequence.current++;
      controller.current?.abort();
      busy.current = false;
    };
  }, [loadOrders]);
  const clearSearch = () => {
    setSearch("");
    setQuery((previous) => ({ ...previous, page: 1, search: "" }));
  };
  const resetFilters = () => {
    setSearch("");
    setQuery(FIRST_QUERY);
  };
  const goToPage = (page) => {
    setQuery((previous) => ({ ...previous, page }));
    heading.current?.focus({ preventScroll: true });
    heading.current?.scrollIntoView?.({ block: "start" });
  };
  const metric = (value) => (value == null ? "—" : value);
  return (
    <DashboardLayout>
      <main className="orders-workspace">
        <header className="orders-heading">
          <div>
            <span className="orders-eyebrow">SUAS PUBLICAÇÕES EM MOVIMENTO</span>
            <h1 ref={heading} tabIndex={-1}>
              Meus pedidos
            </h1>
            <p>Da compra à gráfica, acompanhe cada etapa dos seus livros.</p>
          </div>
          <button
            className="orders-refresh"
            type="button"
            disabled={loading || refreshing}
            onClick={() => loadOrders({ silent: true, refreshPayment: true })}
          >
            {refreshing ? <CircularProgress size={15} color="inherit" /> : <RefreshRoundedIcon />}
            {refreshing ? "Atualizando" : "Atualizar esta página"}
          </button>
        </header>
        <section className="orders-metrics" aria-label="Resumo de todo o histórico de pedidos">
          {[
            ["Pedidos", metric(summary?.total)],
            ["Pagamentos confirmados", metric(summary?.paid)],
            ["Em produção", metric(summary?.producing)],
            ["Valor em pedidos pagos", summary ? currency(summary.value) : "—"],
          ].map(([label, value], i) => (
            <div className={`orders-metric ${i === 2 ? "is-green" : ""}`} key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </section>
        <div className="orders-toolbar">
          <div className="orders-tabs" role="group" aria-label="Filtrar pedidos por pagamento">
            {[
              ["all", "Todos", metric(summary?.total)],
              ["paid", "Pagos", metric(summary?.paid)],
              ["pending", "Aguardando pagamento", metric(summary?.pending)],
            ].map(([key, label, count]) => (
              <button
                type="button"
                key={key}
                aria-pressed={filter === key}
                onClick={() => setQuery((previous) => ({ ...previous, page: 1, payment: key }))}
              >
                {label}
                <b>{count}</b>
              </button>
            ))}
          </div>
          <div className="orders-search">
            <SearchRoundedIcon aria-hidden="true" />
            <input
              type="search"
              maxLength={120}
              aria-label="Buscar pedido, livro ou código PSO"
              placeholder="Buscar pedido, livro ou código PSO"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button type="button" onClick={clearSearch} aria-label="Limpar busca">
                <CloseRoundedIcon />
              </button>
            )}
          </div>
        </div>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            {orders.length > 0 ? " Os pedidos carregados anteriormente continuam disponíveis." : ""}
          </Alert>
        )}
        {loading ? (
          <div className="orders-empty" role="status">
            <CircularProgress size={25} />
            <p>Carregando seus pedidos…</p>
          </div>
        ) : visible.length > 0 ? (
          <section className="orders-list" aria-label="Seus pedidos">
            {visible.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </section>
        ) : (
          <div className="orders-empty">
            <ReceiptLongOutlinedIcon />
            <h2>
              {hasFilters
                ? "Nenhum pedido corresponde à busca"
                : error
                ? "Não foi possível carregar seus pedidos"
                : "Seus livros, novos caminhos"}
            </h2>
            <p>
              {hasFilters
                ? "Tente outro título, código PSO ou filtro."
                : error
                ? "Tente novamente em instantes."
                : "Quando um dos seus livros for comprado, o pedido aparecerá aqui."}
            </p>
            <button
              className="orders-refresh"
              type="button"
              style={{ marginTop: 18 }}
              disabled={refreshing}
              onClick={() =>
                hasFilters ? resetFilters() : loadOrders({ silent: true, discover: !error })
              }
            >
              {hasFilters
                ? "Limpar busca e filtros"
                : error
                ? "Tentar novamente"
                : "Buscar novos pedidos"}
            </button>
          </div>
        )}
        {current && paging.total > 0 && (
          <nav className="orders-pagination" aria-label="Paginação dos pedidos">
            <span role="status">
              {(paging.page - 1) * paging.limit + 1}–
              {(paging.page - 1) * paging.limit + orders.length} de {paging.total} pedidos
            </span>
            <div>
              <button
                type="button"
                disabled={loading || paging.page <= 1}
                onClick={() => goToPage(paging.page - 1)}
              >
                Anterior
              </button>
              <span>
                Página {paging.page} de {paging.totalPages}
              </span>
              <button
                type="button"
                disabled={loading || paging.page >= paging.totalPages}
                onClick={() => goToPage(paging.page + 1)}
              >
                Próxima
              </button>
            </div>
          </nav>
        )}
        <p className="orders-caption">
          20 pedidos por página. Os indicadores consideram todo o histórico sincronizado. O
          andamento da gráfica é atualizado automaticamente.
        </p>
      </main>
    </DashboardLayout>
  );
}
export default MyOrders;
